import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Coffee, Plus, LogOut } from "lucide-react";

/* Social icons hard-coded as inline SVGs at the top of this file so they
 * are baked into the same JS chunk as the Navbar itself — guaranteeing they
 * render in production builds even if a shared SocialIcons component fails
 * to bundle. Do NOT extract into a separate file again. */
const FB_URL  = process.env.REACT_APP_FACEBOOK_URL  || "https://www.facebook.com/VoraStyle512/";
const IG_URL  = process.env.REACT_APP_INSTAGRAM_URL || "https://www.instagram.com/vorastyle512";
const PIN_URL = process.env.REACT_APP_PINTEREST_URL || "https://www.pinterest.com/vorastyle";

function NavSocial({ href, label, testid, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`VoraStyle on ${label}`}
      data-testid={`nav-social-${testid}`}
      className="w-7 h-7 inline-flex items-center justify-center text-[#595959] hover:text-[#9CA47E] hover:bg-[#F2EDE3] transition-colors"
    >
      {children}
    </a>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isEditor = location.pathname.startsWith("/editor");

  if (isEditor) return null;

  const navItem = (to, label, testid) => {
    const active = location.pathname === to || (to !== "/" && location.pathname.startsWith(to));
    return (
      <Link
        to={to}
        data-testid={testid}
        className={`font-body text-[13px] tracking-wide transition-colors hover:text-[#333333] ${active ? "text-[#333333]" : "text-[#595959]"}`}
      >
        {label}
      </Link>
    );
  };

  // "The Curator" — a tighter, editorial pitch for the clipper/extension. Uses
  // the Abstract-V brand icon and an inline subtext for context. The native
  // <title> attribute serves as the hover tooltip — no JS, no layout shift.
  const curatorActive = location.pathname.startsWith("/products");
  const CuratorLink = (
    <Link
      to="/products"
      data-testid="nav-products"
      title="Gather pieces from your favorite sites to curate your VoraStyle collection."
      className="group flex items-center gap-2 transition-colors"
    >
      {/* Abstract V mark */}
      <svg
        viewBox="0 0 20 20"
        aria-hidden="true"
        className={`w-4 h-4 transition-colors ${curatorActive ? "text-[#9CA47E]" : "text-[#9B9387] group-hover:text-[#9CA47E]"}`}
      >
        <path
          d="M3 4 L10 16 L17 4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M6.5 4 L10 11 L13.5 4"
          fill="none"
          stroke="currentColor"
          strokeWidth="0.9"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.55"
        />
      </svg>
      <span className="flex flex-col leading-none">
        <span
          data-testid="nav-curator-label"
          className={`font-heading italic text-[16px] tracking-tight transition-colors ${curatorActive ? "text-[#333333]" : "text-[#333333] group-hover:text-[#9CA47E]"}`}
        >
          The Curator
        </span>
        <span className="font-mono text-[8px] uppercase tracking-[0.28em] text-[#9B9387] group-hover:text-[#9CA47E] transition-colors mt-[1px]">
          Save inspiration
        </span>
      </span>
    </Link>
  );

  return (
    <nav className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-[#EFEFEF]">
      <div className="max-w-[1400px] mx-auto px-6 h-14 grid grid-cols-3 items-center">
        {/* Left: nav links */}
        <div className="hidden md:flex items-center gap-7">
          {navItem("/", "Home", "nav-home")}
          {navItem("/discover", "Journal", "nav-discover")}
          {navItem("/editor", "Studio", "nav-create-link")}
          {CuratorLink}
          <a
            href="https://vorastyle.blogspot.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-blog"
            className="font-body text-[13px] tracking-wide text-[#595959] hover:text-[#333333] transition-colors"
          >
            Blog
          </a>
          {user?.role === "admin" && navItem("/admin", "Admin", "nav-admin")}
        </div>
        <div className="md:hidden" />

        {/* Center: logo */}
        <Link to="/" data-testid="nav-logo" className="flex justify-center items-baseline gap-1.5">
          <span className="font-heading text-[26px] tracking-tight text-[#333333]">VoraStyle</span>
          <span className="hidden lg:inline font-mono text-[9px] uppercase tracking-[0.22em] text-[#595959] ml-1 whitespace-nowrap">curated interiors for aesthetic living</span>
        </Link>

        {/* Right: social icons + actions */}
        <div className="flex items-center justify-end gap-3">
          <div className="hidden sm:flex items-center gap-1 mr-1" data-testid="nav-social-icons">
            <NavSocial href={FB_URL}  label="Facebook"  testid="facebook">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-[13px] h-[13px]" aria-hidden="true">
                <path d="M13.5 22v-8h2.7l.4-3.2H13.5V8.7c0-.9.3-1.6 1.6-1.6h1.7V4.2c-.3 0-1.3-.1-2.4-.1-2.4 0-4 1.5-4 4.1v2.5H7.7V14h2.7v8h3.1z" />
              </svg>
            </NavSocial>
            <NavSocial href={IG_URL}  label="Instagram" testid="instagram">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="w-[13px] h-[13px]" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="5" />
                <circle cx="12" cy="12" r="4" />
                <circle cx="17.3" cy="6.7" r="0.9" fill="currentColor" stroke="none" />
              </svg>
            </NavSocial>
            <NavSocial href={PIN_URL} label="Pinterest" testid="pinterest">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="w-[13px] h-[13px]" aria-hidden="true">
                <circle cx="12" cy="12" r="9.5" />
                <path d="M9.4 21l3-12" />
                <path d="M9.5 14.5c.5 1.4 1.7 2 3 2 2.6 0 4.7-2.3 4.7-5.4 0-3-2.4-5.5-5.8-5.5C7.5 5.6 5 8.3 5 11.5c0 1.7.7 3 2 3.5" />
              </svg>
            </NavSocial>
          </div>

          <Link
            to="/support"
            data-testid="nav-tip"
            className="hidden sm:inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-[#9CA47E] hover:underline underline-offset-4"
          >
            <Coffee className="w-3.5 h-3.5" /> Tip
          </Link>
          <Link
            to="/editor"
            data-testid="nav-create"
            className="hidden md:inline-flex items-center gap-1 px-3 py-1.5 bg-[#9CA47E] text-[#F2EDE3] font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-[#7E876A] transition-colors"
          >
            <Plus className="w-3 h-3" /> Studio
          </Link>
          {user ? (
            <>
              <Link
                data-testid="nav-profile"
                to={`/users/${user.id}`}
                className="hidden sm:flex items-center gap-2 group"
              >
                <span className="w-7 h-7 bg-[#333333] text-white flex items-center justify-center font-mono text-[11px]">
                  {user.username?.[0]?.toUpperCase()}
                </span>
              </Link>
              <button
                data-testid="nav-logout"
                onClick={async () => { await logout(); navigate("/"); }}
                className="text-[#595959] hover:text-[#333333]"
                aria-label="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <Link data-testid="nav-login" to="/login" className="font-body text-[13px] text-[#595959] hover:text-[#333333]">
                Sign in
              </Link>
              <Link
                to="/register"
                data-testid="nav-signup"
                className="hidden sm:inline-flex items-center px-3 py-1.5 border border-[#333333] text-[#333333] font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-[#333333] hover:text-white transition-colors"
              >
                Join
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
